import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${BACKEND_URL}/api`;

const BROKER_SERVERS = [
  // Popular brokers - FivePercentOnline first, FTMO second
  { name: "FivePercentOnline", servers: ["FivePercentOnline-Real", "FivePercentOnline-Demo"] },
  { name: "FTMO", servers: ["FTMO-Server", "FTMO-Demo", "FTMO-Demo2", "FTMO-Demo3"] },
  { name: "Pepperstone", servers: ["Pepperstone-Demo", "Pepperstone-Live", "Pepperstone-Live02"] },
  { name: "IC Markets", servers: ["ICMarkets-Demo01", "ICMarkets-Demo02", "ICMarkets-Live01"] },
  { name: "XM", servers: ["XM-Demo", "XM-Real", "XM-Real 2"] },
  { name: "Exness", servers: ["Exness-Demo", "Exness-Real", "Exness-MT5Real"] },
  { name: "Admiral Markets", servers: ["Admiral-Demo", "Admiral-Live"] },
  { name: "MetaQuotes", servers: ["MetaQuotes-Demo"] },
  { name: "FBS", servers: ["FBS-Demo", "FBS-Real"] },
  { name: "HFM", servers: ["HFM-Demo", "HFM-Real"] },
  { name: "Tickmill", servers: ["Tickmill-Demo", "Tickmill-Live"] },
  { name: "Plus500", servers: ["Plus500-Demo", "Plus500-Live"] }
];

const MT5Connection = ({ setMt5Connected, setMt5Account }) => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [server, setServer] = useState("");
  const [accountType, setAccountType] = useState("Real");
  const [selectedBroker, setSelectedBroker] = useState("");
  const [brokerSearch, setBrokerSearch] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoConnecting, setAutoConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Filter brokers based on search
  const filteredBrokers = BROKER_SERVERS.filter(broker =>
    broker.name.toLowerCase().includes(brokerSearch.toLowerCase())
  );

  // Load saved credentials on mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem("mt5_credentials");
    if (savedCredentials) {
      try {
        const creds = JSON.parse(savedCredentials);
        setLogin(creds.login || "");
        setPassword(creds.password || "");
        setServer(creds.server || "");
        setAccountType(creds.accountType || "Real");
        setSelectedBroker(creds.selectedBroker || "");
        setRememberMe(true);
        
        // Auto-connect if credentials exist
        if (creds.login && creds.password && creds.server) {
          setAutoConnecting(true);
          handleConnect(creds.login, creds.password, creds.server);
        }
      } catch (e) {
        console.error("Error loading credentials:", e);
      }
    }
  }, []);

  const handleBrokerChange = (broker) => {
    setSelectedBroker(broker);
    // Auto-select server based on account type
    const brokerData = BROKER_SERVERS.find(b => b.name === broker);
    if (brokerData) {
      const serverForType = brokerData.servers.find(s => 
        accountType === "Demo" 
          ? s.toLowerCase().includes("demo") 
          : (s.toLowerCase().includes("real") || s.toLowerCase().includes("live") || s.toLowerCase().includes("server"))
      );
      if (serverForType) {
        setServer(serverForType);
      }
    }
  };

  const handleAccountTypeChange = (type) => {
    setAccountType(type);
    // Update server based on selected broker and new account type
    if (selectedBroker) {
      const brokerData = BROKER_SERVERS.find(b => b.name === selectedBroker);
      if (brokerData) {
        const serverForType = brokerData.servers.find(s => 
          type === "Demo" 
            ? s.toLowerCase().includes("demo") 
            : (s.toLowerCase().includes("real") || s.toLowerCase().includes("live") || s.toLowerCase().includes("server"))
        );
        if (serverForType) {
          setServer(serverForType);
        }
      }
    }
  };

  const handleConnect = async (loginVal = null, passwordVal = null, serverVal = null) => {
    const loginToUse = loginVal || login;
    const passwordToUse = passwordVal || password;
    const serverToUse = serverVal || server;

    if (!loginToUse || !passwordToUse || !serverToUse) {
      toast.error("Please fill in all fields");
      setAutoConnecting(false);
      return;
    }

    setLoading(true);
    setConnectionStatus(null);

    try {
      const response = await axios.post(`${API}/mt5/connect`, {
        login: parseInt(loginToUse),
        password: passwordToUse,
        server: serverToUse
      });

      if (response.data.success) {
        setConnectionStatus(response.data);
        setMt5Connected(true);
        setMt5Account(response.data.account);
        
        // Save credentials if remember me is checked
        if (rememberMe) {
          localStorage.setItem("mt5_credentials", JSON.stringify({
            login: loginToUse,
            password: passwordToUse,
            server: serverToUse,
            accountType: accountType,
            selectedBroker: selectedBroker
          }));
        }
        
        toast.success("Successfully connected to MT5!");
      } else {
        setConnectionStatus(response.data);
        toast.error(response.data.message);
      }
    } catch (err) {
      setConnectionStatus({
        success: false,
        message: err.response?.data?.detail || "Connection failed"
      });
      toast.error("Failed to connect to MT5");
    } finally {
      setLoading(false);
      setAutoConnecting(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem("mt5_credentials");
    setLogin("");
    setPassword("");
    setServer("");
    setAccountType("Real");
    setSelectedBroker("");
    setBrokerSearch("");
    setRememberMe(false);
    setConnectionStatus(null);
    setMt5Connected(false);
    setMt5Account(null);
    toast.info("Disconnected from MT5");
  };

  return (
    <div className="forex-calculator-container">
      <div className="forex-header">
        <h1 className="forex-title" data-testid="mt5-title">MetaTrader 5 Connection</h1>
        <p className="forex-subtitle">Connect your MT5 account to place trades automatically</p>
      </div>

      <div className="forex-card">
        <Link to="/" className="nav-link" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", marginBottom: "2rem" }} data-testid="back-to-calculator">
          <ArrowLeft size={20} />
          Back to Calculator
        </Link>

        {autoConnecting && (
          <div style={{ padding: "1rem", background: "rgba(8, 145, 178, 0.1)", borderRadius: "12px", marginBottom: "1.5rem", textAlign: "center" }}>
            <p style={{ color: "#0891b2", fontWeight: "600" }}>Auto-connecting with saved credentials...</p>
          </div>
        )}

        <div className="form-section">
          <label className="form-label">Account Type</label>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              onClick={() => handleAccountTypeChange("Real")}
              style={{
                flex: 1,
                padding: "0.875rem",
                background: accountType === "Real" ? "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)" : "rgba(148, 163, 184, 0.2)",
                border: accountType === "Real" ? "2px solid #0891b2" : "2px solid rgba(148, 163, 184, 0.5)",
                borderRadius: "8px",
                color: accountType === "Real" ? "white" : "#334155",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
              data-testid="real-account-btn"
            >
              Real Account
            </button>
            <button
              onClick={() => handleAccountTypeChange("Demo")}
              style={{
                flex: 1,
                padding: "0.875rem",
                background: accountType === "Demo" ? "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)" : "rgba(148, 163, 184, 0.2)",
                border: accountType === "Demo" ? "2px solid #0891b2" : "2px solid rgba(148, 163, 184, 0.5)",
                borderRadius: "8px",
                color: accountType === "Demo" ? "white" : "#334155",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
              data-testid="demo-account-btn"
            >
              Demo Account
            </button>
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">Find Your Broker</label>
          <div className="risk-input-container">
            <input
              type="text"
              className="risk-input"
              placeholder="Search for your broker..."
              value={brokerSearch}
              onChange={(e) => setBrokerSearch(e.target.value)}
              style={{ paddingLeft: "1rem" }}
              data-testid="broker-search-input"
            />
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">Select Broker</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "0.75rem" }}>
            {filteredBrokers.map((broker) => (
              <button
                key={broker.name}
                onClick={() => handleBrokerChange(broker.name)}
                style={{
                  padding: "0.875rem",
                  background: selectedBroker === broker.name ? "linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)" : "rgba(255, 255, 255, 0.8)",
                  border: selectedBroker === broker.name ? "2px solid #0891b2" : "2px solid rgba(8, 145, 178, 0.3)",
                  borderRadius: "8px",
                  color: "#0f172a",
                  fontSize: "0.875rem",
                  fontWeight: selectedBroker === broker.name ? "700" : "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  textAlign: "center"
                }}
                data-testid={`broker-${broker.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {broker.name}
              </button>
            ))}
          </div>
          {filteredBrokers.length === 0 && (
            <div style={{ padding: "1rem", textAlign: "center", color: "#64748b", fontSize: "0.875rem" }}>
              No brokers found. Try a different search term.
            </div>
          )}
        </div>

        <div className="form-section">
          <label className="form-label">MT5 Login</label>
          <div className="risk-input-container">
            <input
              type="number"
              className="risk-input"
              placeholder="Enter your MT5 login number"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              style={{ paddingLeft: "1rem" }}
              data-testid="mt5-login-input"
            />
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">MT5 Password</label>
          <div className="risk-input-container">
            <input
              type="password"
              className="risk-input"
              placeholder="Enter your MT5 password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingLeft: "1rem" }}
              data-testid="mt5-password-input"
            />
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">MT5 Server</label>
          {selectedBroker && (
            <div style={{ marginBottom: "0.75rem" }}>
              <select
                className="risk-input"
                value={server}
                onChange={(e) => setServer(e.target.value)}
                style={{ paddingLeft: "1rem" }}
                data-testid="mt5-server-select"
              >
                <option value="">Select {accountType} server for {selectedBroker}</option>
                {BROKER_SERVERS.find(b => b.name === selectedBroker)?.servers.map(srv => (
                  <option key={srv} value={srv}>{srv}</option>
                ))}
              </select>
            </div>
          )}
          <input
            type="text"
            className="risk-input"
            placeholder="Or enter custom server name"
            value={server}
            onChange={(e) => setServer(e.target.value)}
            style={{ paddingLeft: "1rem" }}
            data-testid="mt5-server-input"
          />
          {server && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#0891b2", fontWeight: "600" }}>
              Selected: {server}
            </div>
          )}
        </div>

        <div className="form-section">
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <input
              type="checkbox"
              id="remember-me"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ width: "1.25rem", height: "1.25rem", cursor: "pointer" }}
              data-testid="remember-me-checkbox"
            />
            <label htmlFor="remember-me" style={{ fontSize: "0.95rem", color: "#334155", fontWeight: "600", cursor: "pointer" }}>
              Remember my credentials and auto-connect
            </label>
          </div>
          <div style={{ fontSize: "0.875rem", color: "#64748b", marginTop: "0.5rem" }}>
            Your credentials will be saved securely in your browser
          </div>
        </div>

        <div style={{ display: "flex", gap: "1rem" }}>
          <button
            className="calculate-btn"
            onClick={() => handleConnect()}
            disabled={loading || autoConnecting}
            data-testid="connect-mt5-btn"
            style={{ flex: 1 }}
          >
            {loading || autoConnecting ? "Connecting..." : "Connect to MT5"}
          </button>
          
          {connectionStatus?.success && (
            <button
              className="calculate-btn"
              onClick={handleDisconnect}
              style={{ flex: 1, background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" }}
              data-testid="disconnect-mt5-btn"
            >
              Disconnect
            </button>
          )}
        </div>

        {connectionStatus && (
          <div
            className={connectionStatus.success ? "result-card" : "error-message"}
            style={{ marginTop: "2rem" }}
            data-testid="connection-status"
          >
            {connectionStatus.success ? (
              <div>
                <h3 style={{ color: "#4ade80", marginBottom: "1rem", fontSize: "1.5rem" }} data-testid="connection-success">✓ Connected Successfully</h3>
                <div className="result-grid">
                  <div className="result-item">
                    <div className="result-label">Login</div>
                    <div className="result-value" data-testid="account-login">{connectionStatus.account.login}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Balance</div>
                    <div className="result-value" data-testid="account-balance">${connectionStatus.account.balance}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Equity</div>
                    <div className="result-value" data-testid="account-equity">${connectionStatus.account.equity}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Currency</div>
                    <div className="result-value" data-testid="account-currency">{connectionStatus.account.currency}</div>
                  </div>
                </div>
              </div>
            ) : (
              <p data-testid="connection-error">{connectionStatus.message}</p>
            )}
          </div>
        )}

        <div style={{ marginTop: "2rem", padding: "1.5rem", background: "rgba(59, 130, 246, 0.1)", borderRadius: "12px", border: "1px solid rgba(59, 130, 246, 0.3)" }}>
          <h4 style={{ color: "#60a5fa", marginBottom: "1rem", fontSize: "1.125rem" }}>Quick Demo Setup Guide:</h4>
          <ol style={{ color: "#334155", lineHeight: "1.8", paddingLeft: "1.5rem" }}>
            <li>Open MetaTrader 5 mobile app or desktop terminal</li>
            <li>Create a demo account with FTMO, Pepperstone, or other broker</li>
            <li>Copy your login number from the app (shown at top)</li>
            <li>Enter the password you set during registration</li>
            <li>Select your broker's demo server from dropdown</li>
            <li>Check "Remember me" for auto-connect next time</li>
          </ol>
          <div style={{ marginTop: "1rem", padding: "1rem", background: "rgba(16, 185, 129, 0.1)", borderRadius: "8px", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
            <p style={{ color: "#059669", fontWeight: "600", fontSize: "0.875rem" }}>
              ✓ Your credentials are saved in your browser only (not on our servers)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MT5Connection;